import { Box, Container, Slider, TextField } from "@mui/material";
import { SmartDatePicker, SmartDateTimePicker } from "@nodeon/react-components";
import dayjs, { Dayjs } from "dayjs";
import isBetween from "dayjs/plugin/isBetween";
import { useSnackbar } from "notistack";
import React from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import Player from "video.js/dist/types/player";
import { CameraSaveClipDto } from "~/api/GeneratedApi";
import { useCameraClips } from "./hooks/useCameraClips";
import { sliderGetAriaValueText, VideoClipProps } from "./VideoClip";

export type ClippingBoxProps = {
  videoPlayer: Player | null;
  startDateTime: Date;
  videoSliderValue: number[];
  onClose: () => void;
  endTime: Date;
} & Pick<VideoClipProps, "cameraId">;

dayjs.extend(isBetween);

export const ClippingBox = ({
  cameraId,
  videoPlayer,
  startDateTime,
  videoSliderValue,
  onClose,
  endTime,
  saveCamClipAsync,
}: ClippingBoxProps &
  Pick<ReturnType<typeof useCameraClips>, "saveCamClipAsync">) => {
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const [clipSliderValue, setClipSliderValue] =
    React.useState<number[]>(videoSliderValue);
  const [maxTime] = React.useState(clipSliderValue[1]);
  const [minTime] = React.useState(clipSliderValue[0]);

  const {
    register,
    handleSubmit,
    control,
    setValue,

    // formState: { errors },
  } = useForm<CameraSaveClipDto>({
    defaultValues: {
      expirationTime: dayjs(new Date()).add(1, "month").toDate(),
      startTime: dayjs(startDateTime)
        .add(clipSliderValue[0], "seconds")
        .toDate(),
      endTime,
    },
  });
  // React.useEffect(() => {
  //   setValue(
  //     "startTime",
  //     dayjs(dateTime).add(clipSliderValue[0], "seconds").toDate(),
  //   );
  //   setEndTimeValue(dayjs(dateTime).add(clipSliderValue[1], "seconds"));
  // }, [clipSliderValue, dateTime, setEndTimeValue, setValue]);

  const maxDate = React.useMemo(
    () => dayjs(startDateTime).add(maxTime, "seconds").toDate(),
    [startDateTime, maxTime],
  );

  const handleTimeInputChange = React.useCallback(
    (writtenDate: Date, timeType: "startTime" | "endTime") => {
      if (timeType === "startTime") {
        setClipSliderValue((val) => {
          const newVal = dayjs(writtenDate).diff(startDateTime, "seconds");
          return [newVal, val[1]];
        });
      } else if (timeType === "endTime") {
        setClipSliderValue((val) => {
          const newVal = dayjs(writtenDate).diff(startDateTime, "seconds");
          return [val[0], newVal];
        });
      }
    },
    [startDateTime],
  );

  const { t } = useTranslation("");

  const onSubmit = (
    data: CameraSaveClipDto,
    e: React.FormEvent<HTMLFormElement>,
  ) => {
    e.preventDefault();
    const { endTime: clipEndtime, startTime, ...rest } = data;
    const roudedEndtime = dayjs(clipEndtime).startOf("minutes").toDate();
    const roundedStartTime = dayjs(startTime).startOf("minutes").toDate();
    const roundedData: CameraSaveClipDto = {
      endTime: roudedEndtime,
      startTime: roundedStartTime,
      // expirationTime:
      //   expirationTime ?? dayjs(new Date()).add(1, "month").toDate(),
      ...rest,
    };

    // check every date value before submit
    const invalidDate = Object.keys(roundedData).some((k) => {
      const key = k as keyof typeof roundedData;
      if (
        roundedData[key] instanceof Date &&
        !dayjs(roundedData[key]).isValid()
      ) {
        return true;
      }
      return false;
    });

    if (invalidDate) {
      const key = enqueueSnackbar(t(`clips:invalidDates`), {
        variant: "warning",
        preventDuplicate: true,
        onClick: () => closeSnackbar(key),
      });
    } else {
      void saveCamClipAsync({ camId: cameraId, clipInfo: roundedData }).then(
        () => {
          const key = enqueueSnackbar(t(`general:created`), {
            variant: "success",
            preventDuplicate: true,
            onClick: () => closeSnackbar(key),
          });
          onClose();
        },
      );
    }
  };

  const setEndTimeValue = React.useCallback(
    (date: Dayjs) => {
      // const newDate = date.add(-1, "ms");
      setValue("endTime", date.toDate());
    },
    [setValue],
  );

  const handleSliderChange = React.useCallback(
    (val: number[]) => {
      setValue(
        "startTime",
        dayjs(startDateTime).add(val[0], "seconds").toDate(),
      );
      setEndTimeValue(dayjs(startDateTime).add(val[1], "seconds"));
      setClipSliderValue(val);
    },
    [startDateTime, setEndTimeValue, setValue],
  );
  const handleTimeChange = React.useCallback(
    (date: Date, timeType: "startTime" | "endTime") => {
      if (date instanceof Date && !Number.isNaN(date)) {
        setValue(timeType, date);
        if (dayjs(date).isBetween(startDateTime, maxDate))
          handleTimeInputChange(date, timeType);
      }
      // if (e.value && dayjs(e.value, "HH:mm:ss", true).isValid()) {
      //   setValue(e.name, e.value);
      // }
    },
    [startDateTime, handleTimeInputChange, maxDate, setValue],
  );
  if (!videoPlayer) return null;

  return (
    <Container>
      <form
        id="create-clip-form"
        // eslint-disable-next-line @typescript-eslint/no-misused-promises
        onSubmit={handleSubmit((data, e) =>
          onSubmit(data, e as React.FormEvent<HTMLFormElement>),
        )}
        // onSubmit={(e) => handleSubmit((data, e) =>
        //   onSubmit(data, e as React.FormEvent<HTMLFormElement>),
        // )}
        style={{ display: "flex", flexDirection: "column", gap: "16px" }}
      >
        <TextField
          label={t("general:name")}
          {...register("name", { required: true })}
          sx={{ width: "100%" }}
        />

        <TextField
          label={t("event:description")}
          multiline
          {...register("description", { required: true })}
          sx={{ width: "100%" }}
        />
        <Box>
          <Box sx={{ display: "flex", flexDirection: "row", gap: "16px" }}>
            <SmartDateTimePicker
              label={t("vessels:startTime")}
              name="startTime"
              controllerProps={{ control }}
              maxDateTime={maxDate}
              minDateTime={startDateTime}
              disableOpenPicker
              onChange={(d) => handleTimeChange(d as Date, "startTime")}
              required
              //  inputFormat="HH:mm"
              // value={dayjs(startT).format("HH:mm:ss")}
            />

            <SmartDateTimePicker
              label={t("vessels:endTime")}
              name="endTime"
              controllerProps={{ control }}
              maxDateTime={maxDate}
              minDateTime={startDateTime}
              disableOpenPicker
              required
              //  inputFormat="HH:mm"
              onChange={(d) => handleTimeChange(d as Date, "endTime")}

              // value={dayjs(startT).format("HH:mm:ss")}
            />
          </Box>
          <Slider
            onChange={(_e, val) => handleSliderChange(val as number[])}
            value={clipSliderValue}
            valueLabelDisplay="auto"
            max={maxTime}
            min={minTime}
            //  getAriaValueText={() => "moi"}
            valueLabelFormat={(val: number) =>
              sliderGetAriaValueText(val, startDateTime, "HH:mm")
            }
          />
        </Box>
        <Box>
          <SmartDatePicker<CameraSaveClipDto>
            label={t("clips:expirationTime")}
            name="expirationTime"
            controllerProps={{ control }}
            // value={dayjs(new Date()).add(1, "month").toDate()}
            // onChange={(d) => handleTimeChange(d as Date, "expirationTime")}
            // InputProps={{ sx: { width: "50%" } }}
          />
        </Box>
      </form>
    </Container>
  );
};
